import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tab,
  Tabs,
} from '@mui/material';
import { Close, Download } from '@mui/icons-material';

interface CardViewModalProps {
  open: boolean;
  onClose: () => void;
  cardId: string | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CardViewModal: React.FC<CardViewModalProps> = ({
  open,
  onClose,
  cardId,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  
  const frontImageUrl = cardId ? `${apiUrl}/get-image/${cardId}/front` : '';
  const backImageUrl = cardId ? `${apiUrl}/get-image/${cardId}/back` : '';
  const pdfUrl = cardId ? `${apiUrl}/get-pdf/${cardId}` : '';

  useEffect(() => {
    if (open && cardId) {
      setLoading(false);
      setError(null);
    }
  }, [open, cardId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDownloadPDF = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  if (!cardId) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '600px',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ flexGrow: 1 }}>Card Preview</Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="card views">
                <Tab label="Front" />
                <Tab label="Back" />
              </Tabs>
            </Box>
            
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ textAlign: 'center' }}>
                <img
                  src={frontImageUrl}
                  alt="Card Front"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '500px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  }}
                  onError={() => setError('Failed to load card front image')}
                />
              </Box>
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ textAlign: 'center' }}>
                <img
                  src={backImageUrl}
                  alt="Card Back"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '500px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  }}
                  onError={() => setError('Failed to load card back image')}
                />
              </Box>
            </TabPanel>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={handleDownloadPDF}
          disabled={!pdfUrl}
        >
          Download PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CardViewModal;